import React, { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Image } from 'react-bootstrap';

import queryString from 'query-string';

import AuthButton from 'components/base/AuthButton';
import CustomBreadcrumb from 'components/base/CustomBreadcrumb';
import CustomListComponent from 'components/base/CustomListComponent';
import DeletePrompt from 'components/base/DeletePrompt';
import { DELETE_RELATED_SECTIONS, EarningRuleType } from 'config/CustomEnums';
import { NavBarNames } from 'config/NavBarNameList';
import { PermissionCodes } from 'config/PermissionCodes';
import BaseListContainer from 'containers/base/BaseListContainer';
import BaseTabListContainer from 'containers/base/BaseTabListContainer';

import Filter from './Filter';

import { checkHasPermission, createAction } from 'utils';

import './EarningRuleList.scss';
import ListButtonsGroup from 'components/base/ListButtonsGroup';
import GlobalSettingPrompt from 'components/earning/GlobalSettingPrompt';
import CloseButton from '../../../assets/images/campaignStreamline/close.png';
import BasePrompt from 'components/base/prompt/BasePrompt';

function EarningRuleList({
  user,
  listFields,
  dataList,
  pageInfo,
  totalPage,
  totalCount,
  checkedList,
  hideEarningRuleTip,
}) {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();

  const [showTips, setShowTips] = useState(!hideEarningRuleTip);
  const [showPrompt, setShowPrompt] = useState(false);

  const displayListFields = useMemo(() => {
    const parsedSearch = queryString.parse(location.search);
    if (parsedSearch?.type) {
      return listFields
    }
    return listFields?.filter(item => item?.displayName !== "Execution priority")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  const buttons = [
    <AuthButton
      title="Create Earning Rules"
      action={() => {
        setShowPrompt(true);
      }}
      requires={PermissionCodes.addEarningRule}
    />
  ];

  const tabs = [
    {
      name: 'List Of Earnning Rules',
      content: (
        <BaseTabListContainer
          hideTab={false}
          maxTabWidth={670}
          tabs={[
            { key: EarningRuleType.allTypes, name: 'All Type' },
            { key: EarningRuleType.qrCodeScanning, name: 'QR Code' },
            { key: EarningRuleType.gpsCheckIn, name: 'GPS' },
            { key: EarningRuleType.memberReferral, name: 'Referral' },
            { key: EarningRuleType.generalPurchase, name: 'Purchase' },
            { key: EarningRuleType.newMember, name: 'New Member' },
            { key: EarningRuleType.birthday, name: 'Birthday' },
            { key: EarningRuleType.fillingForm, name: 'Webhook' },
            { key: EarningRuleType.levelUpgrade, name: 'Level Upgrade' },
          ]}
          groupActions={[]}
          // defaultTabKey={levels}
          pageInfo={pageInfo}
          totalCount={totalCount}
          filter={{ hasFilter: true, component: Filter }}
          listContent={
            <BaseListContainer
              fields={displayListFields}
              dataList={dataList}
              totalPage={totalPage}
              model={'earningRuleList'}
              permissionGroup={PermissionCodes.earningrule}
              deleteInfo={{
                data: [],
                title: 'earning rules',
                relatedName: 'campaigns',
                onComfirm: {},
                relatedSections: DELETE_RELATED_SECTIONS.EARNING_RULE,
              }}
              actions={['Detail']}
            />
          }
        />
      ),
    },
  ];

  useEffect(() => {
    dispatch({ type: 'settings/getAdminTipConfig' });
    dispatch(
      createAction('segments/getAllList')({
        type: 'all',
        isSelectorLoad: true,
        moreSearch: {
          isBlacklist: false,
        }
      }),
    );
    dispatch({ type: 'levels/getLevelList' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const parsedSearch = queryString.parse(location.search);
    const type = parsedSearch['type'] || '';
    const searchKey = parsedSearch['search'] || '';
    const stringRank = parsedSearch['rank'] || 'true';
    const stringPage = parsedSearch['page'] || 1;
    const targetCustomer = parsedSearch['target_customer'] || '';
    const dynamicSegmentsIn = parsedSearch['dynamicSegmentsIn'] || '';
    const staticSegmentsIn = parsedSearch['staticSegmentsIn'] || '';
    const levelsIn = parsedSearch['levelsIn'] || '';
    const rewardType = parsedSearch['reward_type'] || '';
    const sort = parsedSearch['sort'] || '';
    const rank = stringRank === 'true';
    const page = parseInt(stringPage);

    dispatch({
      type: 'earningRuleList/getEarningRuleListWithTypes',
      payload: {
        ...parsedSearch,
        page,
        reverse: rank,
        search: searchKey,
        otherSearch: {
          rewardType,
          targetCustomer,
          dynamicSegmentsIn,
          staticSegmentsIn,
          levelsIn,
          sort,
        },
        type: type, //filters.levels.join(','),
      },
    });
  }, [dispatch, location]);

  return (
    <>
      <CustomListComponent
        caution={{
          detail: (
            <div className='earning-rule-tips'>
              <Image
                src={CloseButton}
                className="tips-close"
                onClick={() => {
                  setShowTips(false);
                }}
              />
              <span>
                Earning rules are now integrated with campaigns;
                creating, editing, and deleting earning rules are all
                managed within the Campaigns section.
              </span>
              <span
                className='do-not-display-again'
                onClick={() => {
                  setShowTips(false);
                  dispatch({
                    type: 'settings/updateAdminTipConfig',
                    payload: {
                      data: {
                        hideEarningRuleTip: true,
                      }
                    }
                  })
                }}
              >
                Do not display again
              </span>
            </div>
          ),
          title: NavBarNames.earnRules,
          extraProps: hideEarningRuleTip ? {} : { show: showTips },
        }}
        defaultActiveKey={tabs[0].name}
        breadcrumb={<CustomBreadcrumb />}
        buttons={buttons}
        tabs={tabs}
        hideTab={true}
      />

      <BasePrompt
        show={showPrompt}
        closeAction={() => setShowPrompt(false)}
        title={"Earning Rule Creation Moved to Campaigns"}
        description={`Earning rules are now created together with campaigns in the Campaigns section. To create an earning rule, please go to the Campaigns section and select "Create Campaign". You will have the option to choose whether the campaign is displayed on the front end. Editing and deleting earning rules are also done within the Campaigns section.`}
        leftButton={{
          text: 'Cancel',
          action: () => {
            setShowPrompt(false);
          },
        }}
        rightButton={{
          text: 'Go to Campaigns',
          action: () => {
            history.push({
              pathname: '/campaigns',
            });
          },
        }}
      />
    </>
  );
}

const mapPropsToState = (state) => ({
  listFields: state.earningRuleList.listDisplayFields,
  dataList: state.earningRuleList.earningRuleTypeList,
  pageInfo: state.earningRuleList.pageInfo,
  totalPage: state.earningRuleList.totalPage,
  totalCount: state.earningRuleList.totalCount,
  checkedList: state.earningRuleList.checkedList,
  user: state.users,
  hideEarningRuleTip: state.settings.adminTipConfig?.hideEarningRuleTip,
});

export default connect(mapPropsToState)(EarningRuleList);
